import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Clock, ShieldAlert } from 'lucide-react';
import websocketService from '../../services/websocketService';
import {
  allowSessionChallenge,
  rejectSessionChallenge,
} from '../../api/authService';
import { performLogout } from '../../services/logoutService';

/**
 * react-hot-toast only exposes toast / success / error / loading / custom — not toast.info.
 * Using the default "blank" toast avoids production crashes (TypeError: lib.info is not a function).
 */
function toastNeutral(message, options) {
  return toast(message, { icon: 'ℹ️', duration: 4000, ...options });
}

/**
 * Denominator for the progress bar: server window while the server deadline is still ahead,
 * otherwise the local decision window (matches {@link computeRemainingMs}).
 */
function computeProgressDenominatorMs(dialog, now = Date.now()) {
  const decisionSeconds = dialog.decisionSeconds ?? 30;
  const receivedAtMs = dialog.receivedAtMs;
  const localSpanMs = Math.max(1, decisionSeconds * 1000);

  if (!dialog.expiresAt) {
    return localSpanMs;
  }
  const serverDeadline = new Date(dialog.expiresAt).getTime();
  if (Number.isNaN(serverDeadline)) {
    return localSpanMs;
  }
  if (serverDeadline > now && serverDeadline > receivedAtMs) {
    return Math.max(1, serverDeadline - receivedAtMs);
  }
  return localSpanMs;
}

function computeProgressPercent(dialog, now = Date.now()) {
  const remainingMs = computeRemainingMs(dialog, now);
  const denomMs = computeProgressDenominatorMs(dialog, now);
  return Math.min(100, Math.max(0, (remainingMs / denomMs) * 100));
}

/**
 * Milliseconds remaining in the decision window (continuous, no second rounding).
 * Prefer server expiresAt; if invalid or already elapsed, use local window from receivedAtMs.
 */
function computeRemainingMs(dialog, now = Date.now()) {
  const decisionSeconds = dialog.decisionSeconds ?? 30;
  const receivedAtMs = dialog.receivedAtMs;
  const localEndMs = receivedAtMs + decisionSeconds * 1000;

  if (!dialog.expiresAt) {
    return Math.max(0, localEndMs - now);
  }
  const serverDeadline = new Date(dialog.expiresAt).getTime();
  if (Number.isNaN(serverDeadline)) {
    return Math.max(0, localEndMs - now);
  }
  if (serverDeadline <= now) {
    return Math.max(0, localEndMs - now);
  }
  return Math.max(0, serverDeadline - now);
}

/**
 * User A (existing session): listens for NEW_LOGIN_REQUEST when policy is PENDING_USER_A.
 * With PENDING_USER_B, login does not publish this event and this listener stays idle.
 */
export default function SessionConflictListener() {
  const navigate = useNavigate();
  const handledIdsRef = useRef(new Set());
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const progressFillRef = useRef(null);

  const closeDialog = useCallback((challengeId, removeFromHandled) => {
    setDialog(null);
    setBusy(false);
    if (removeFromHandled && challengeId) {
      handledIdsRef.current.delete(challengeId);
    }
  }, []);

  // Smooth progress bar via rAF (ms-accurate width each frame; no second rounding)
  useEffect(() => {
    if (!dialog) {
      return undefined;
    }

    let cancelled = false;
    let expiryHandled = false;
    let rafId = 0;
    const challengeId = dialog.challengeId;

    const step = () => {
      if (cancelled) {
        return;
      }
      const now = Date.now();
      const remainingMs = computeRemainingMs(dialog, now);
      const pct = computeProgressPercent(dialog, now);

      const el = progressFillRef.current;
      if (el) {
        el.style.width = `${pct}%`;
      }

      if (remainingMs <= 0) {
        if (!expiryHandled) {
          expiryHandled = true;
          toastNeutral('Sign-in approval window ended.', {
            id: `login-conflict-expired-${challengeId}`,
          });
          closeDialog(challengeId, true);
        }
        return;
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [dialog, closeDialog]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!dialog) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dialog]);

  const onReject = async () => {
    if (!dialog || busy) return;
    setBusy(true);
    try {
      await rejectSessionChallenge(dialog.challengeId);
      toast.success('Sign-in attempt rejected.');
      closeDialog(dialog.challengeId, true);
    } catch (e) {
      toast.error(e?.message || 'Could not reject sign-in.');
      setBusy(false);
    }
  };

  const onAllow = async () => {
    if (!dialog || busy) return;
    setBusy(true);
    const challengeId = dialog.challengeId;
    try {
      await allowSessionChallenge(challengeId);
      closeDialog(challengeId, true);
      toast.success('You approved the sign-in and have been signed out on this device.');

      await performLogout({ navigate, reason: 'session-conflict' });
    } catch (e) {
      toast.error(e?.message || 'Could not allow sign-in.');
      setBusy(false);
    }
  };

  useEffect(() => {
    const onNewLoginRequest = (message) => {
      const challengeId = message?.challengeId;
      if (!challengeId) {
        return;
      }
      if (handledIdsRef.current.has(challengeId)) {
        return;
      }
      handledIdsRef.current.add(challengeId);

      const text =
        message?.message || 'Another sign-in attempt was detected for your account.';
      const expiresAt =
        typeof message?.expiresAt === 'string' && message.expiresAt.length > 0
          ? message.expiresAt
          : null;
      const decisionSeconds =
        typeof message?.decisionSeconds === 'number' && Number.isFinite(message.decisionSeconds)
          ? message.decisionSeconds
          : 30;

      const receivedAtMs = Date.now();
      let totalWindowSec = Math.max(1, decisionSeconds);
      if (expiresAt) {
        const deadline = new Date(expiresAt).getTime();
        if (!Number.isNaN(deadline) && deadline > receivedAtMs) {
          totalWindowSec = Math.max(1, Math.ceil((deadline - receivedAtMs) / 1000));
        }
      }

      setDialog({
        challengeId,
        message: text,
        expiresAt,
        decisionSeconds,
        totalWindowSec,
        receivedAtMs,
      });
    };

    const attach = () => {
      websocketService.unsubscribe('NEW_LOGIN_REQUEST', onNewLoginRequest);
      websocketService.subscribe('NEW_LOGIN_REQUEST', onNewLoginRequest);
    };

    attach();
    window.addEventListener('websocket-connected', attach);
    return () => {
      window.removeEventListener('websocket-connected', attach);
      websocketService.unsubscribe('NEW_LOGIN_REQUEST', onNewLoginRequest);
    };
  }, []);

  if (!dialog) {
    return null;
  }

  const initialProgressPct = computeProgressPercent(dialog);

  const modal = (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-conflict-title"
      aria-describedby="session-conflict-desc"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
        aria-hidden="true"
      />
      {/* Card */}
      <div
        className="relative z-10 w-full max-w-md rounded-2xl border-2 border-amber-400 bg-white p-6 shadow-2xl ring-4 ring-amber-300/40 dark:border-amber-500 dark:bg-slate-900 dark:ring-amber-500/30 animate-in zoom-in-95 fade-in duration-200"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <ShieldAlert className="h-6 w-6" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="session-conflict-title"
              className="text-lg font-semibold text-slate-900 dark:text-white"
            >
              New login attempt
            </h2>
            <p
              id="session-conflict-desc"
              className="mt-1 text-sm text-slate-600 dark:text-slate-300"
            >
              {dialog.message}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-4 dark:border-amber-800 dark:bg-amber-950/40">
          <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
            <Clock className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-sm font-medium">Time remaining to decide</span>
          </div>
          <div
            className="mt-3 h-3 w-full overflow-hidden rounded-full bg-amber-200/90 dark:bg-amber-900/60"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(initialProgressPct)}
            aria-live="off"
            aria-label="Time remaining in the approval window"
          >
            <div
              ref={progressFillRef}
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-400 dark:to-amber-500"
              style={{ width: `${initialProgressPct}%` }}
            />
          </div>
        </div>

        {/* <p className="mt-4 text-center text-[11px] text-slate-400 dark:text-slate-500 break-all">
          Reference: {dialog.challengeId}
        </p> */}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            onClick={onReject}
          >
            Reject
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onAllow}
            title="Approve the other sign-in and sign out on this browser"
          >
            {busy ? 'Please wait…' : 'Allow & sign out'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
